<script lang="ts" setup>
import type { PropType } from 'vue'
import Button from 'primevue/button'
import type { CheckCartItem } from '../../types/checkCart'

const props = defineProps({
  cart: {
    type: Array as PropType<CheckCartItem[]>,
    required: true,
  },
  showActions: {
    type: Boolean,
    default: true,
  },
  compactView: {
    type: Boolean,
    default: false,
  },
})

const { t } = useNuxtApp().$i18n
const posCheckStore = usePosCheckStore()
const {
  removeItemFromCheckCart,
  voidItemFromCheckCart,
  increaseItemQuantity,
  decreaseItemQuantity,
  editProductInCheckCart,
  editComboInCheckCart,
  sendToKitchen,
} = posCheckStore

// Void dialog state
const showVoidDialog = ref(false)
const voidItemUuid = ref<string | null>(null)

function handleVoidClick(cartUuid: string) {
  voidItemUuid.value = cartUuid
  showVoidDialog.value = true
}

async function handleVoidConfirm(data: { reason: string, notes: string }) {
  if (voidItemUuid.value) {
    await voidItemFromCheckCart(voidItemUuid.value, data.reason, data.notes)
    voidItemUuid.value = null
  }
}

// Group items by kitchen status (newest first)
const unsentItems = computed(() => props.cart.filter(item => item.kitchen_status === 'not_sent').reverse())
const sentItems = computed(() => props.cart.filter(item => item.kitchen_status === 'sent_to_kitchen').reverse())
const readyItems = computed(() => props.cart.filter(item => item.kitchen_status === 'ready').reverse())
const servedItems = computed(() => props.cart.filter(item => item.kitchen_status === 'served').reverse())
const voidedItems = computed(() => props.cart.filter(item => item.kitchen_status === 'voided').reverse())
const canceledItems = computed(() => props.cart.filter(item => item.kitchen_status === 'canceled').reverse())

// Helper to check if a sent item has been modified
const hasModifications = (item: CheckCartItem): boolean => {
  if (!item.kitchen_history || item.kitchen_history.length === 0)
    return false
  const lastAction = item.kitchen_history[item.kitchen_history.length - 1]
  return lastAction?.action === 'modified'
}

// Voided and canceled items section collapsed state
const voidedItemsCollapsed = ref(true)
const canceledItemsCollapsed = ref(true)
</script>

<template>
  <div class="rounded bg-white shadow-lg">
    <!-- Unsent Items Section -->
    <div v-if="unsentItems.length > 0">
      <div class="bg-gray-100 p-1 px-2 border-b border-gray-300">
        <h3 class="text-sm font-semibold text-gray-700 flex items-center gap-2 m-0">
          <i class="pi pi-clock text-orange-600" />
          {{ t('Unsent Items') }} ({{ unsentItems.length }})
        </h3>
      </div>
      <ul class="m-0 list-none pl-0">
        <li
          v-for="(item) in unsentItems"
          :key="`unsent-${item.cart_uuid}`"
          class="p-2 odd:bg-gray-50"
        >
          <!-- Product Renderer -->
          <POSCheckProductItemRenderer
            v-if="item.type === 'PRODUCT'"
            :item="item"
            :show-actions="showActions"
            :compact-view="compactView"
            context="cart"
            @increase="increaseItemQuantity"
            @decrease="decreaseItemQuantity"
            @edit="editProductInCheckCart"
            @remove="removeItemFromCheckCart"
            @void="handleVoidClick"
          />

          <!-- Combo Renderer -->
          <POSCheckComboItemRenderer
            v-else-if="item.type === 'COMBO'"
            :item="item"
            :show-actions="showActions"
            :compact-view="compactView"
            context="cart"
            @increase="increaseItemQuantity"
            @decrease="decreaseItemQuantity"
            @edit="editComboInCheckCart"
            @remove="removeItemFromCheckCart"
            @void="handleVoidClick"
          />

          <!-- Custom Combo Renderer -->
          <POSCheckCustomComboItemRenderer
            v-else-if="item.type === 'CUSTOM_COMBO'"
            :item="item"
            :show-actions="showActions"
            :compact-view="compactView"
            context="cart"
            @increase="increaseItemQuantity"
            @decrease="decreaseItemQuantity"
            @edit="editComboInCheckCart"
            @remove="removeItemFromCheckCart"
            @void="handleVoidClick"
          />
        </li>
      </ul>
      <!-- Send to Kitchen Button -->
      <div class="p-3 bg-gray-50 border-t border-gray-300">
        <Button
          :label="t('kds.send')"
          icon="pi pi-send"
          severity="success"
          class="w-full text-base font-semibold py-3"
          @click="sendToKitchen"
        />
      </div>
    </div>

    <!-- Sent to Kitchen Section -->
    <div v-if="sentItems.length > 0">
      <div class="bg-blue-100 p-1 px-2 border-b border-blue-300">
        <h3 class="text-sm font-semibold text-blue-800 flex items-center gap-2 m-0">
          <i class="pi pi-check-circle text-blue-600" />
          {{ t('Sent to Kitchen') }} ({{ sentItems.length }})
        </h3>
      </div>
      <ul class="m-0 list-none pl-0">
        <li
          v-for="(item) in sentItems"
          :key="`sent-${item.cart_uuid}`"
          class="p-2 odd:bg-blue-50 bg-blue-25 opacity-75 relative"
        >
          <!-- Modified Badge -->
          <div
            v-if="hasModifications(item)"
            class="absolute top-2 right-2 bg-amber-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1"
            :title="t('Item modified after being sent to kitchen')"
          >
            <i class="pi pi-pencil text-xs" />
            {{ t('Modified') }}
          </div>
          <!-- Product Renderer -->
          <POSCheckProductItemRenderer
            v-if="item.type === 'PRODUCT'"
            :item="item"
            :show-actions="showActions"
            :compact-view="compactView"
            context="cart"
            @increase="increaseItemQuantity"
            @decrease="decreaseItemQuantity"
            @edit="editProductInCheckCart"
            @remove="removeItemFromCheckCart"
            @void="handleVoidClick"
          />

          <!-- Combo Renderer -->
          <POSCheckComboItemRenderer
            v-else-if="item.type === 'COMBO'"
            :item="item"
            :show-actions="showActions"
            :compact-view="compactView"
            context="cart"
            @increase="increaseItemQuantity"
            @decrease="decreaseItemQuantity"
            @edit="editComboInCheckCart"
            @remove="removeItemFromCheckCart"
            @void="handleVoidClick"
          />

          <!-- Custom Combo Renderer -->
          <POSCheckCustomComboItemRenderer
            v-else-if="item.type === 'CUSTOM_COMBO'"
            :item="item"
            :show-actions="showActions"
            :compact-view="compactView"
            context="cart"
            @increase="increaseItemQuantity"
            @decrease="decreaseItemQuantity"
            @edit="editComboInCheckCart"
            @remove="removeItemFromCheckCart"
            @void="handleVoidClick"
          />
        </li>
      </ul>
    </div>

    <!-- Ready Items Section -->
    <div v-if="readyItems.length > 0">
      <div class="bg-green-100 p-1 px-2 border-b border-green-300">
        <h3 class="text-sm font-semibold text-green-800 flex items-center gap-2 m-0">
          <i class="pi pi-check text-green-600" />
          {{ t('Ready Items') }} ({{ readyItems.length }})
        </h3>
      </div>
      <ul class="m-0 list-none pl-0">
        <li
          v-for="(item) in readyItems"
          :key="`ready-${item.cart_uuid}`"
          class="p-2 odd:bg-green-50 bg-green-25 opacity-75"
        >
          <!-- Product Renderer -->
          <POSCheckProductItemRenderer
            v-if="item.type === 'PRODUCT'"
            :item="item"
            :show-actions="false"
            :compact-view="compactView"
            context="cart"
          />

          <!-- Combo Renderer -->
          <POSCheckComboItemRenderer
            v-else-if="item.type === 'COMBO'"
            :item="item"
            :show-actions="false"
            :compact-view="compactView"
            context="cart"
          />

          <!-- Custom Combo Renderer -->
          <POSCheckCustomComboItemRenderer
            v-else-if="item.type === 'CUSTOM_COMBO'"
            :item="item"
            :show-actions="false"
            :compact-view="compactView"
            context="cart"
          />
        </li>
      </ul>
    </div>

    <!-- Served Items Section -->
    <div v-if="servedItems.length > 0">
      <div class="bg-gray-100 p-1 px-2 border-b border-gray-300">
        <h3 class="text-sm font-semibold text-gray-700 flex items-center gap-2 m-0">
          <i class="pi pi-user text-gray-600" />
          {{ t('Served Items') }} ({{ servedItems.length }})
        </h3>
      </div>
      <ul class="m-0 list-none pl-0">
        <li
          v-for="(item) in servedItems"
          :key="`served-${item.cart_uuid}`"
          class="p-2 odd:bg-gray-50 opacity-60"
        >
          <!-- Product Renderer -->
          <POSCheckProductItemRenderer
            v-if="item.type === 'PRODUCT'"
            :item="item"
            :show-actions="false"
            :compact-view="compactView"
            context="cart"
          />

          <!-- Combo Renderer -->
          <POSCheckComboItemRenderer
            v-else-if="item.type === 'COMBO'"
            :item="item"
            :show-actions="false"
            :compact-view="compactView"
            context="cart"
          />

          <!-- Custom Combo Renderer -->
          <POSCheckCustomComboItemRenderer
            v-else-if="item.type === 'CUSTOM_COMBO'"
            :item="item"
            :show-actions="false"
            :compact-view="compactView"
            context="cart"
          />
        </li>
      </ul>
    </div>

    <!-- Voided Items Section -->
    <div v-if="voidedItems.length > 0">
      <div
        class="bg-red-100 p-1 px-2 border-b border-red-300 flex items-center justify-between cursor-pointer"
        @click="voidedItemsCollapsed = !voidedItemsCollapsed"
      >
        <h3 class="text-sm font-semibold text-red-800 flex items-center gap-2 m-0">
          <i class="pi pi-times-circle text-red-600" />
          {{ t('Voided Items') }} ({{ voidedItems.length }})
        </h3>
        <button class="text-red-800 hover:text-red-900 transition-colors p-1">
          <i
            :class="voidedItemsCollapsed ? 'pi pi-plus' : 'pi pi-minus'"
            class="text-xs"
          />
        </button>
      </div>
      <ul
        v-if="!voidedItemsCollapsed"
        class="m-0 list-none pl-0"
      >
        <li
          v-for="(item) in voidedItems"
          :key="`voided-${item.cart_uuid}`"
          class="p-2 odd:bg-red-50 bg-red-25 opacity-75"
        >
          <!-- Product Renderer -->
          <POSCheckProductItemRenderer
            v-if="item.type === 'PRODUCT'"
            :item="item"
            :show-actions="false"
            :compact-view="compactView"
            context="cart"
          />

          <!-- Combo Renderer -->
          <POSCheckComboItemRenderer
            v-else-if="item.type === 'COMBO'"
            :item="item"
            :show-actions="false"
            :compact-view="compactView"
            context="cart"
          />

          <!-- Custom Combo Renderer -->
          <POSCheckCustomComboItemRenderer
            v-else-if="item.type === 'CUSTOM_COMBO'"
            :item="item"
            :show-actions="false"
            :compact-view="compactView"
            context="cart"
          />
        </li>
      </ul>
    </div>

    <!-- Canceled Items Section -->
    <div v-if="canceledItems.length > 0">
      <div
        class="bg-gray-100 p-1 px-2 border-b border-gray-300 flex items-center justify-between cursor-pointer"
        @click="canceledItemsCollapsed = !canceledItemsCollapsed"
      >
        <h3 class="text-sm font-semibold text-gray-600 flex items-center gap-2 m-0">
          <i class="pi pi-ban text-gray-500" />
          {{ t('Canceled Items') }} ({{ canceledItems.length }})
        </h3>
        <button class="text-gray-600 hover:text-gray-800 transition-colors p-1">
          <i
            :class="canceledItemsCollapsed ? 'pi pi-plus' : 'pi pi-minus'"
            class="text-xs"
          />
        </button>
      </div>
      <ul
        v-if="!canceledItemsCollapsed"
        class="m-0 list-none pl-0"
      >
        <li
          v-for="(item) in canceledItems"
          :key="`canceled-${item.cart_uuid}`"
          class="p-2 odd:bg-gray-50 opacity-50"
        >
          <!-- Product Renderer -->
          <POSCheckProductItemRenderer
            v-if="item.type === 'PRODUCT'"
            :item="item"
            :show-actions="false"
            :compact-view="compactView"
            context="cart"
          />

          <!-- Combo Renderer -->
          <POSCheckComboItemRenderer
            v-else-if="item.type === 'COMBO'"
            :item="item"
            :show-actions="false"
            :compact-view="compactView"
            context="cart"
          />

          <!-- Custom Combo Renderer -->
          <POSCheckCustomComboItemRenderer
            v-else-if="item.type === 'CUSTOM_COMBO'"
            :item="item"
            :show-actions="false"
            :compact-view="compactView"
            context="cart"
          />

          <!-- Cancel Info Badge -->
          <div
            v-if="item.cancel_info"
            class="text-xs text-gray-500 italic mt-1 pl-2"
          >
            {{ t('Canceled') }}: {{ t(item.cancel_info.reason) }}
            <span v-if="item.cancel_info.notes"> - {{ item.cancel_info.notes }}</span>
          </div>
        </li>
      </ul>
    </div>

    <!-- Edit Modals -->
    <POSEditCheckProductEditModal />
    <POSEditCheckComboEditModal />

    <!-- Void Dialog -->
    <POSVoidItemDialog
      v-model:visible="showVoidDialog"
      @confirm="handleVoidConfirm"
    />
  </div>
</template>
